import { useCallback, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  downloadMediaState,
  inputMediaNamesState,
  mediaShownInComparerState,
} from '@/stores/mediaStore';
import { useTracker } from '@/core/tracking';
import { pushModalSelector } from '@/stores/modalStore';
import { useRouter } from 'next/router';
import taskToFormatAssociationState from '@/stores/formatState';
import { Format } from '@/services/formatService/formatTypes';
import ResetFormatModal from '@/features/downloadMedia/downloadSidebar/modals/ResetFormatModal';
import JSZip from 'jszip';
import {
  trackCompletedEvent,
  trackFailedEvent,
  trackStartedEvent,
} from '@/core/monitoring/trackEvents';
import type useDownload from '../useDownload';

type useBatchDownloadTabArg = Pick<
  ReturnType<typeof useDownload>,
  'download' | 'reprocessIfNeeded'
>;

export default function useBatchDownloadTab({
  download,
  reprocessIfNeeded,
}: useBatchDownloadTabArg) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const pushModal = useSetRecoilState(pushModalSelector);

  const router = useRouter();
  const { taskId: bulkUploadId } = router.query;

  const { track } = useTracker();

  const mediasToDownload = useRecoilValue(downloadMediaState);
  const orderedMediasToDownload = useMemo(
    () =>
      [...mediasToDownload].sort((a, b) =>
        (a.bulkPosition || 0) <= (b.bulkPosition || 0) ? -1 : 1
      ),
    [mediasToDownload]
  );
  const mediaInComparer = useRecoilValue(mediaShownInComparerState);

  const [taskToFormatAssociation, setTaskToFormatAssociation] = useRecoilState(
    taskToFormatAssociationState
  );

  const mediaNames = useRecoilValue(inputMediaNamesState);

  const areMultipleFormatsSelected = useMemo(() => {
    if (mediasToDownload.length === 1) {
      return false;
    }

    const selectedMediasIds = mediasToDownload.map((media) => media.id);
    const allFormats = Object.entries(taskToFormatAssociation)
      .filter(([mediaId]) => selectedMediasIds.includes(mediaId))
      .map(([, format]) => format);

    return allFormats.some((format) => format.label !== allFormats[0].label);
  }, [mediasToDownload, taskToFormatAssociation]);

  const setFormatForAllTasks = useCallback(
    (format: Format) => {
      setTaskToFormatAssociation((prev) => ({
        ...prev,
        ...Object.fromEntries(
          mediasToDownload.map((selectedMedia) => [selectedMedia.id, format])
        ),
      }));
    },
    [mediasToDownload, setTaskToFormatAssociation]
  );

  const onFormatSelection = useCallback(
    (format: Format) => {
      if (areMultipleFormatsSelected) {
        pushModal({
          id: 'reset-multiple-formats',
          Modal: ResetFormatModal,
          overwriteAllFormats: () => {
            setFormatForAllTasks(format);
          },
        });
      } else {
        setFormatForAllTasks(format);
      }
    },
    [areMultipleFormatsSelected, pushModal, setFormatForAllTasks]
  );

  const onBatchDownload = useCallback(async () => {
    track('batch_download', 'tapped', {
      bulkUploadId,
      mediaQuantity: orderedMediasToDownload.length,
    });

    setIsLoading(true);
    const fullDownloadResponse = await reprocessIfNeeded();

    const zip = new JSZip();
    const folder = zip.folder('images');
    if (!folder) {
      return;
    }
    const fileNames: string[] = [];

    const downloadUrlEventPayload = { type: 'batch' };
    const downloadUrlSingleEventPayload = { type: 'single_in_batch_fetch' };

    // track start event here
    trackStartedEvent(['download', 'url'], downloadUrlEventPayload);

    orderedMediasToDownload.forEach((media) => {
      const mediaIndex = media.bulkPosition!;
      const outputFormat = taskToFormatAssociation[media.id!];

      let fileName = `${mediaNames[mediaIndex]}.${outputFormat.extension}`;
      if (fileNames.includes(fileName)) {
        fileName = `${mediaNames[mediaIndex]} (${fileNames.length}).${outputFormat.extension}`;
      }
      fileNames.push(fileName);

      const downloadUrl = fullDownloadResponse[media.id!].finalOutputUrl;

      trackStartedEvent(['download', 'url'], downloadUrlSingleEventPayload);
      const blobPromise = fetch(downloadUrl).then((resp) => {
        if (resp.status === 200) {
          trackCompletedEvent(
            ['download', 'url'],
            downloadUrlSingleEventPayload
          );
          return resp.blob();
        }

        trackFailedEvent(['download', 'url'], {
          ...downloadUrlSingleEventPayload,
          error: resp.statusText,
        });
        return Promise.reject(new Error(resp.statusText));
      });
      folder.file(fileName, blobPromise);
    });

    let blob;
    try {
      blob = await zip.generateAsync({ type: 'blob' });
      trackCompletedEvent(['download', 'url'], downloadUrlEventPayload);
    } catch (e) {
      // if one download fails, blob fails
      trackFailedEvent(['download', 'url'], {
        ...downloadUrlEventPayload,
        error: e?.toString?.(),
      });
      setIsLoading(false);
      throw e;
    }

    if (blob) {
      await download({ blob, fileName: 'remini-batch-enhance.zip' });
    }
    setIsLoading(false);
  }, [
    bulkUploadId,
    download,
    mediaNames,
    orderedMediasToDownload,
    reprocessIfNeeded,
    taskToFormatAssociation,
    track,
  ]);

  const batchDownloadFormat = useMemo(
    () =>
      orderedMediasToDownload.length > 0
        ? taskToFormatAssociation[orderedMediasToDownload[0].id!]
        : taskToFormatAssociation[mediaInComparer.id!],
    [mediaInComparer.id, orderedMediasToDownload, taskToFormatAssociation]
  );

  return {
    areMultipleFormatsSelected,
    batchDownloadFormat,
    isLoading: isLoading || orderedMediasToDownload.length === 0,
    onBatchDownload,
    onFormatSelection,
    orderedMediasToDownload,
    taskToFormatAssociation,
  };
}
